import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { primaryLedgerService } from '@/services/primaryLedgerService';
import { PrimaryLedgerCompany } from '@/types/primaryLedger';

interface PrimaryLedgerContextValue {
  companies: PrimaryLedgerCompany[];
  selectedCompany: string | null;
  selectedAsin: string | null;
  asins: string[];
  isLoadingCompanies: boolean;
  isLoadingAsins: boolean;
  setSelectedCompany: (company: string | null) => void;
  setSelectedAsin: (asin: string | null) => void;
}

const PrimaryLedgerContext = createContext<PrimaryLedgerContextValue | undefined>(undefined);

export const usePrimaryLedger = () => {
  const context = useContext(PrimaryLedgerContext);
  if (!context) {
    throw new Error('usePrimaryLedger must be used within PrimaryLedgerProvider');
  }
  return context;
};

interface PrimaryLedgerProviderProps {
  children: ReactNode;
}

export const PrimaryLedgerProvider: React.FC<PrimaryLedgerProviderProps> = ({ children }) => {
  const [companies, setCompanies] = useState<PrimaryLedgerCompany[]>([]);
  const [selectedCompany, setSelectedCompanyState] = useState<string | null>(null);
  const [selectedAsin, setSelectedAsinState] = useState<string | null>(null);
  const [asins, setAsins] = useState<string[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingAsins, setIsLoadingAsins] = useState(false);
  const [isContextReady, setIsContextReady] = useState(false);

  useEffect(() => {
    console.log('[PrimaryLedger] Subscribing to companies');
    const unsubscribe = primaryLedgerService.subscribeToCompanies((updatedCompanies) => {
      console.log('[PrimaryLedger] Companies loaded:', updatedCompanies.length, 'companies');
      setCompanies(updatedCompanies);
      setIsLoadingCompanies(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (companies.length > 0 && !isContextReady) {
      console.log('[PrimaryLedger] Initializing context with', companies.length, 'companies');
      const persistedCompany = localStorage.getItem('primaryLedger_selectedCompany');
      const persistedAsin = localStorage.getItem('primaryLedger_selectedAsin');
      
      console.log('[PrimaryLedger] Persisted company:', persistedCompany);
      console.log('[PrimaryLedger] Persisted ASIN:', persistedAsin);
      
      if (persistedCompany) {
        const companyExists = companies.some(c => c.id === persistedCompany);
        console.log('[PrimaryLedger] Company exists in list:', companyExists);
        
        if (companyExists) {
          console.log('[PrimaryLedger] Restoring valid persisted company');
          setSelectedCompanyState(persistedCompany);
          if (persistedAsin) {
            setSelectedAsinState(persistedAsin);
          }
        } else {
          console.warn('[PrimaryLedger] Persisted company not found in list, clearing:', persistedCompany);
          localStorage.removeItem('primaryLedger_selectedCompany');
          localStorage.removeItem('primaryLedger_selectedAsin');
        }
      }
      
      console.log('[PrimaryLedger] Context initialization complete');
      setIsContextReady(true);
    }
  }, [companies, isContextReady]);

  useEffect(() => {
    if (!isContextReady) return;
    
    if (selectedCompany) {
      localStorage.setItem('primaryLedger_selectedCompany', selectedCompany);
    } else {
      localStorage.removeItem('primaryLedger_selectedCompany');
    }
  }, [selectedCompany, isContextReady]);

  useEffect(() => {
    if (!isContextReady) return;
    
    if (selectedAsin) {
      localStorage.setItem('primaryLedger_selectedAsin', selectedAsin);
    } else {
      localStorage.removeItem('primaryLedger_selectedAsin');
    }
  }, [selectedAsin, isContextReady]);

  useEffect(() => {
    const loadAsins = async () => {
      console.log('[PrimaryLedger] loadAsins effect triggered. isContextReady:', isContextReady, 'selectedCompany:', selectedCompany);
      
      if (!isContextReady) {
        console.log('[PrimaryLedger] Context not ready, skipping loadAsins');
        return;
      }

      if (!selectedCompany) {
        console.log('[PrimaryLedger] No company selected, clearing ASINs');
        setAsins([]);
        return;
      }

      if (companies.length === 0) {
        console.log('[PrimaryLedger] Companies not loaded yet, skipping loadAsins');
        return;
      }

      const company = companies.find(c => c.id === selectedCompany);
      if (!company) {
        console.warn('[PrimaryLedger] Company not found in list, clearing selection:', selectedCompany);
        setSelectedCompanyState(null);
        setSelectedAsinState(null);
        localStorage.removeItem('primaryLedger_selectedCompany');
        localStorage.removeItem('primaryLedger_selectedAsin');
        setAsins([]);
        return;
      }

      console.log('[PrimaryLedger] Loading ASINs for company:', company.companyName);
      setIsLoadingAsins(true);
      try {
        const companyAsins = await primaryLedgerService.getCompanyAsins(
          selectedCompany,
          company.companyName
        );
        console.log('[PrimaryLedger] Loaded', companyAsins.length, 'ASINs');
        setAsins(companyAsins);
      } catch (error) {
        console.error('[PrimaryLedger] Error loading ASINs:', error);
        setAsins([]);
      } finally {
        setIsLoadingAsins(false);
      }
    };

    loadAsins();
  }, [selectedCompany, companies, isContextReady]);

  const setSelectedCompany = (company: string | null) => {
    setSelectedCompanyState(company);
    setSelectedAsinState(null);
  };

  const setSelectedAsin = (asin: string | null) => {
    setSelectedAsinState(asin);
  };

  const value: PrimaryLedgerContextValue = {
    companies,
    selectedCompany,
    selectedAsin,
    asins,
    isLoadingCompanies,
    isLoadingAsins,
    setSelectedCompany,
    setSelectedAsin,
  };

  return (
    <PrimaryLedgerContext.Provider value={value}>
      {children}
    </PrimaryLedgerContext.Provider>
  );
};